<!--
  An ElOption with the same style as entity segments

  As close as possible from element-ui option
  https://github.com/ElemeFE/element/blob/dev/packages/select/src/option.vue
-->

<template>
  <li
    @mouseenter="hoverItem"
    @click.stop="selectOptionClick"
    class="el-select-dropdown__item el-entity-select-dropdown__item"
    v-show="visible"
    :class="{
      'selected': itemSelected,
      'is-disabled': disabled || groupDisabled || limitReached,
      'hover': hover
    }">
    <span :class="['el-entity-option', entityClass]" :style="entityStyle">
      <span class="el-entity-option__prefix">{{ entityPrefix }}</span>
      <slot>{{ currentLabel }}</slot>
    </span>
    <span class="el-entity-option__info">{{' '}}
      <template v-if="info">{{ info }}</template>
      <slot v-else name="info"></slot>
    </span>
  </li>
</template>

<script>
import Emitter from 'element-ui/src/mixins/emitter'
import { getValueByPath, escapeRegexpString } from 'element-ui/src/utils/util'

export default {
  mixins: [Emitter],

  name: 'ElEntityOption',

  componentName: 'ElOption',

  inject: ['select'],

  props: {
    value: {
      required: true,
    },
    item: {},
    label: [String, Number],
    created: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
    // the type of entity, changes its style
    type: String,
    // the prefix to show
    prefix: String,
    // the info to show next to the name
    info: String,
    // the class to use
    className: String,
    // the style to apply
    styleObj: Object,
    // more direct suggestion system
    suggestions: Array,
  },

  data () {
    return {
      index: -1,
      groupDisabled: false,
      visible: true,
      hitState: false,
      hover: false,
    }
  },

  computed: {
    isObject () {
      return Object.prototype.toString.call(this.value).toLowerCase() === '[object object]'
    },

    currentLabel () {
      return this.label || (this.isObject ? '' : this.value)
    },

    currentValue () {
      return this.value || this.label || ''
    },

    itemSelected () {
      if (!this.select.multiple) {
        return this.isEqual(this.value, this.select.value)
      } else {
        return this.contains(this.select.value, this.value)
      }
    },

    limitReached () {
      if (this.select.multiple) {
        return !this.itemSelected &&
            (this.select.value || []).length >= this.select.multipleLimit &&
            this.select.multipleLimit > 0
      } else {
        return false
      }
    },
    entityType () {
      return this.type || this.select.type
    },
    entityPrefix () {
      if (this.prefix) return this.prefix
      else if (this.select.prefix) return this.select.prefix
      else if (this.entityType === 'entity') return '@'
      else if (this.entityType === 'synonym') return '#'
      else if (this.entityType === 'variable') return '$'
      else return ''
    },
    entityClass () {
      return this.className || this.select.className ||
        this.entityType && 'el-entity-autocomplete__item-' + this.entityType || ''
    },
    entityStyle () {
      return this.styleObj || this.select.styleObj
    },
  },

  watch: {
    currentLabel () {
      if (!this.created && !this.select.remote) this.dispatch('ElSelect', 'setSelected')
    },
    value (val, oldVal) {
      const { remote, valueKey } = this.select
      if (!this.created && !remote) {
        if (valueKey && typeof val === 'object' && typeof oldVal === 'object' && val[valueKey] === oldVal[valueKey]) {
          return
        }
        this.dispatch('ElSelect', 'setSelected')
      }
    },
  },

  methods: {
    isEqual (a, b) {
      if (!this.isObject) {
        return a === b
      } else {
        const valueKey = this.select.valueKey
        return getValueByPath(a, valueKey) === getValueByPath(b, valueKey)
      }
    },

    contains (arr = [], target) {
      if (!this.isObject) {
        return arr.indexOf(target) > -1
      } else {
        const valueKey = this.select.valueKey
        return arr.some(item => {
          return getValueByPath(item, valueKey) === getValueByPath(target, valueKey)
        })
      }
    },

    handleGroupDisabled (val) {
      this.groupDisabled = val
    },

    hoverItem () {
      if (!this.disabled && !this.groupDisabled) {
        this.select.hoverIndex = this.select.options.indexOf(this)
      }
    },

    selectOptionClick () {
      if (this.disabled !== true && this.groupDisabled !== true) {
        this.dispatch('ElSelect', 'handleOptionClick', [this, true])
      }
    },

    queryChange (query) {
      this.visible = new RegExp(escapeRegexpString(query), 'i').test(this.currentLabel) || this.created
      if (!this.visible) {
        this.select.filteredOptionsCount--
      }
    },
  },

  created () {
    this.select.options.push(this)
    this.select.cachedOptions.push(this)
    this.select.optionsCount++
    this.select.filteredOptionsCount++

    this.$on('queryChange', this.queryChange)
    this.$on('handleGroupDisabled', this.handleGroupDisabled)
  },

  beforeDestroy () {
    this.select.onOptionDestroy(this.select.options.indexOf(this))
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  /* style of entities */
  .el-entity-option__entity {
    background-color: $entity-bg-color;
  }
  .el-entity-option__synonym {
    background-color: $synonym-bg-color;
  }
  .el-entity-option__variable {
    background-color: $variable-bg-color;
  }
  .el-entity-option__info {
    color: $--color-info;
  }
  /* removes the ugly blue color of selected item */
  .el-select-dropdown__item.el-entity-select-dropdown__item.selected {
    color: inherit;
  }
</style>
